import type { ComponentType } from 'react';
import { defineAuthProvider } from '@zg-rentals/auth-react/legacy';
import { isHotpads, isZillow } from '@zg-rentals/multibrand-support';
import type { Brand } from '@zg-rentals/multibrand-support';
import { zillowLoginRedirectHref } from '@zg-rentals/login-zillow/client';
import { CometUserAuthenticationWebHandler_getUser_queryOptions } from '@zg-rentals/ts-satellite-client-react-query';
import type { AuthCheckUserInfoDto } from '@zg-rentals/ts-satellite-client';
import Custom500 from './pages/500';

export type User = AuthCheckUserInfoDto;

const Auth = defineAuthProvider<{ brand: Brand }, User>({
  queryOptions: () => CometUserAuthenticationWebHandler_getUser_queryOptions({}),

  isAuthorized: (user, { brand }) => {
    if (!user.isLoggedIn) return false;
    // HotPads has a "soft-login" state where the user is logged in but not authorized.
    // In this case userToken is absent, so we treat the user as unauthorized and let
    // AuthBoundary's onUnauthorized callback redirect them to /user/update-password.
    if (isHotpads(brand) && !user.userToken) return false;
    return true;
  },
});

function getBoundaryProps(brand: Brand) {
  return {
    loading: null,
    onError: <Custom500 brand={brand} />,
    redirectHref: isZillow(brand)
      ? zillowLoginRedirectHref({
          baseURL: '/',
          entryPoint: 'satellite-rachel-web',
        })
      : undefined,
    onUnauthorized: isHotpads(brand)
      ? (user: User) => {
          if (typeof window !== 'undefined') {
            if (!user.isLoggedIn) {
              const currentPath = window.location.pathname + window.location.search;
              window.location.href = `/login?redirect=${encodeURIComponent(currentPath)}`;
            } else if (!user.userToken && process.env.NODE_ENV !== 'development') {
              window.location.href = `${window.location.origin}/user/update-password?redirect=/renter-hub/inbox`;
            }
          }
          return null;
        }
      : undefined,
  };
}

export function withAuthBoundary<
  Props extends { user: Exclude<ReturnType<typeof Auth.useAuth>['user'], null>; brand: Brand },
>(Component: ComponentType<Props>, boundaryProps?: { loading?: React.ReactNode }) {
  type OuterProps = Omit<Props, 'user'>;

  const WithAuthBoundary = (props: OuterProps) => (
    <Auth.Boundary {...getBoundaryProps(props.brand)} {...boundaryProps}>
      {(user) => <Component {...(props as Props)} user={user} />}
    </Auth.Boundary>
  );

  WithAuthBoundary.displayName = `WithAuthBoundary(${Component.displayName || Component.name})`;

  return WithAuthBoundary;
}

export default Auth;

export const useAuth = Auth.useAuth;
